import React, { useState } from 'react';
import { Search, Download, Eye, Upload, FileText, User, Calendar, Shield, Filter, X } from 'lucide-react';

const HRDocumentManager = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewingDocument, setViewingDocument] = useState(null);

  // Sample employee data
  const employees = [
    {
      id: 1,
      name: 'Sarah Johnson',
      department: 'Engineering',
      position: 'Senior Developer',
      avatar: 'SJ',
      joinDate: '2022-03-15',
      documentCount: 12
    },
    {
      id: 2,
      name: 'Michael Chen',
      department: 'Marketing',
      position: 'Marketing Manager',
      avatar: 'MC',
      joinDate: '2021-08-20',
      documentCount: 8
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      department: 'HR',
      position: 'HR Specialist',
      avatar: 'ER',
      joinDate: '2023-01-10',
      documentCount: 15
    },
    {
      id: 4,
      name: 'David Wilson',
      department: 'Finance',
      position: 'Financial Analyst',
      avatar: 'DW',
      joinDate: '2022-11-05',
      documentCount: 6
    }
  ];

  // Sample documents
  const documents = {
    1: [
      { id: 101, name: 'Employment Contract', type: 'contract', uploadDate: '2022-03-15', size: '245 KB', confidential: true },
      { id: 102, name: 'Tax Forms W-4', type: 'tax', uploadDate: '2022-03-20', size: '89 KB', confidential: true },
      { id: 103, name: 'Performance Review Q1 2024', type: 'review', uploadDate: '2024-04-01', size: '156 KB', confidential: false },
      { id: 104, name: 'Training Certificate - React', type: 'certificate', uploadDate: '2024-02-14', size: '78 KB', confidential: false },
      { id: 105, name: 'Medical Insurance Form', type: 'insurance', uploadDate: '2023-01-15', size: '234 KB', confidential: true }
    ],
    2: [
      { id: 201, name: 'Employment Agreement', type: 'contract', uploadDate: '2021-08-20', size: '298 KB', confidential: true },
      { id: 202, name: 'Marketing Campaign Report', type: 'report', uploadDate: '2024-01-30', size: '1.2 MB', confidential: false },
      { id: 203, name: 'Performance Review 2023', type: 'review', uploadDate: '2024-01-15', size: '189 KB', confidential: false }
    ],
    3: [
      { id: 301, name: 'HR Policy Manual', type: 'manual', uploadDate: '2023-01-10', size: '567 KB', confidential: false },
      { id: 302, name: 'Employee Handbook Signed', type: 'handbook', uploadDate: '2023-01-12', size: '234 KB', confidential: false },
      { id: 303, name: 'Confidentiality Agreement', type: 'contract', uploadDate: '2023-01-10', size: '123 KB', confidential: true }
    ],
    4: [
      { id: 401, name: 'Financial Analysis Certification', type: 'certificate', uploadDate: '2022-11-05', size: '145 KB', confidential: false },
      { id: 402, name: 'Employment Contract', type: 'contract', uploadDate: '2022-11-05', size: '234 KB', confidential: true }
    ]
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEmployeeDocuments = (employeeId) => {
    const docs = documents[employeeId] || [];
    if (filterType === 'all') return docs;
    return docs.filter(doc => doc.type === filterType);
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      contract: 'bg-red-100 text-red-800',
      tax: 'bg-blue-100 text-blue-800',
      review: 'bg-green-100 text-green-800',
      certificate: 'bg-purple-100 text-purple-800',
      insurance: 'bg-orange-100 text-orange-800',
      report: 'bg-indigo-100 text-indigo-800',
      manual: 'bg-gray-100 text-gray-800',
      handbook: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const DocumentViewer = ({ document, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{document.name}</h3>
              <p className="text-sm text-gray-500">Uploaded on {document.uploadDate} • {document.size}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 h-96 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Document preview would appear here</p>
            <p className="text-sm text-gray-500 mt-2">In a real implementation, this would show the actual document content</p>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Employee Document Management</h1>
          <p className="text-gray-600">Manage and view employee documents securely</p>
        </div>

        <div className="flex gap-6">
          {/* Employee List Sidebar */}
          <div className="w-96 bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {filteredEmployees.map((employee) => (
                <button
                  key={employee.id}
                  onClick={() => setSelectedEmployee(employee)}
                  className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedEmployee?.id === employee.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {employee.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{employee.name}</h3>
                      <p className="text-sm text-gray-600 truncate">{employee.position}</p>
                      <p className="text-xs text-gray-500">{employee.department} • {employee.documentCount} docs</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {selectedEmployee ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                {/* Employee Header */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {selectedEmployee.avatar}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.name}</h2>
                        <p className="text-gray-600">{selectedEmployee.position}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center space-x-1">
                            <User className="w-4 h-4" />
                            <span>{selectedEmployee.department}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>Joined {selectedEmployee.joinDate}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                      <Upload className="w-4 h-4" />
                      <span>Upload Document</span>
                    </button>
                  </div>
                </div>

                {/* Document Filters */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Documents</option>
                      <option value="contract">Contracts</option>
                      <option value="tax">Tax Forms</option>
                      <option value="review">Reviews</option>
                      <option value="certificate">Certificates</option>
                      <option value="insurance">Insurance</option>
                    </select>
                  </div>
                </div>

                {/* Documents List */}
                <div className="p-6">
                  <div className="space-y-3">
                    {getEmployeeDocuments(selectedEmployee.id).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-center space-x-4">
                          <FileText className="w-8 h-8 text-blue-600" />
                          <div>
                            <div className="flex items-center space-x-3">
                              <h3 className="font-semibold text-gray-900">{doc.name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(doc.type)}`}>
                                {doc.type}
                              </span>
                              {doc.confidential && (
                                <span className="flex items-center space-x-1 text-red-600 text-xs">
                                  <Shield className="w-3 h-3" />
                                  <span>Confidential</span>
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">Uploaded {doc.uploadDate} • {doc.size}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewingDocument(doc)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View Document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download Document"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {getEmployeeDocuments(selectedEmployee.id).length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">No documents found for the selected filter</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm h-96 flex items-center justify-center">
                <div className="text-center">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Select an Employee</h3>
                  <p className="text-gray-500">Choose an employee from the sidebar to view their documents</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Document Viewer Modal */}
        {viewingDocument && (
          <DocumentViewer 
            document={viewingDocument} 
            onClose={() => setViewingDocument(null)} 
          />
        )}
      </div>
    </div>
  );
};

export default HRDocumentManager;